import React, { useEffect, useMemo, useRef, useState } from "react";
import { DollarSign, Eye, EyeOff, GripVertical, X, TrendingUp } from "lucide-react";

const fmt = (n) =>
    (Number(n) || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

/**
 * Floating, draggable "Cost vs Profit" breakdown popup.
 * Shown ONLY to the estimator — the pane never reaches the client.
 *
 * Reads ``raw_subtotal`` / ``raw_markup`` / ``raw_grand_total`` off the
 * estimate record (persisted server-side at generation time so the
 * frontend can always recover the pre-margin totals) and lets the user
 * drag the margin slider to preview the final price in real time. No
 * network round-trip — the recompute happens locally the moment the
 * slider changes.
 */
export default function CostProfitPopup({ report, onChangeMargin }) {
    const [open, setOpen] = useState(false);
    const [pct, setPct] = useState(() => Number(report?.profit_margin_pct ?? 0));
    useEffect(() => {
        setPct(Number(report?.profit_margin_pct ?? 0));
    }, [report?.id, report?.profit_margin_pct]);

    const rawGrand = Number(report?.raw_grand_total ?? report?.totals?.grand_total ?? 0);
    const rawSubtotal = Number(report?.raw_subtotal ?? report?.totals?.subtotal ?? 0);
    const rawMarkup = Number(report?.raw_markup ?? report?.totals?.markup_total ?? 0);

    const { profitAmount, finalTotal } = useMemo(() => {
        const profit = +(rawGrand * (pct / 100)).toFixed(2);
        return { profitAmount: profit, finalTotal: +(rawGrand + profit).toFixed(2) };
    }, [rawGrand, pct]);

    // --- drag-to-move handling ---
    const wrapRef = useRef(null);
    const [pos, setPos] = useState({ x: 24, y: 120 });
    const dragRef = useRef({ active: false, startX: 0, startY: 0, origX: 0, origY: 0 });
    const onMouseDown = (e) => {
        dragRef.current = {
            active: true,
            startX: e.clientX,
            startY: e.clientY,
            origX: pos.x,
            origY: pos.y,
        };
        e.preventDefault();
    };
    useEffect(() => {
        const move = (e) => {
            if (!dragRef.current.active) return;
            const dx = e.clientX - dragRef.current.startX;
            const dy = e.clientY - dragRef.current.startY;
            const maxX = Math.max(0, window.innerWidth - 360);
            const maxY = Math.max(0, window.innerHeight - 80);
            const nx = Math.max(8, Math.min(maxX, dragRef.current.origX + dx));
            const ny = Math.max(8, Math.min(maxY, dragRef.current.origY + dy));
            setPos({ x: nx, y: ny });
        };
        const up = () => {
            dragRef.current.active = false;
        };
        window.addEventListener("mousemove", move);
        window.addEventListener("mouseup", up);
        return () => {
            window.removeEventListener("mousemove", move);
            window.removeEventListener("mouseup", up);
        };
    }, []);

    const commit = () => {
        if (!onChangeMargin) return;
        if (Math.abs(pct - Number(report?.profit_margin_pct ?? 0)) < 0.01) return;
        onChangeMargin({ profit_margin_pct: pct, profit_amount: profitAmount, final_total: finalTotal });
    };

    if (!open) {
        return (
            <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="cost-profit-popup-open"
                className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-amber-500 text-slate-900 font-bold text-xs uppercase tracking-wider shadow-lg shadow-amber-500/30 hover:bg-amber-400 transition-colors"
                style={{ fontFeatureSettings: '"tnum"' }}
            >
                <DollarSign className="w-4 h-4" />
                Cost vs profit
            </button>
        );
    }

    return (
        <div
            ref={wrapRef}
            data-testid="cost-profit-popup"
            className="fixed z-50 w-[340px] bg-[#0A1128] border border-amber-500/40 rounded-md shadow-2xl shadow-black/60 text-white"
            style={{ left: pos.x, top: pos.y }}
        >
            <div
                onMouseDown={onMouseDown}
                className="flex items-center gap-2 px-3 py-2 border-b border-[#1C253C] cursor-grab active:cursor-grabbing bg-amber-500/10"
            >
                <GripVertical className="w-4 h-4 text-amber-300 shrink-0" />
                <div className="flex-1 min-w-0 flex items-center gap-1.5">
                    <DollarSign className="w-3.5 h-3.5 text-amber-300" />
                    <span className="text-[11px] font-bold uppercase tracking-wider text-amber-200">
                        Cost vs profit · private
                    </span>
                </div>
                <button
                    onClick={() => setOpen(false)}
                    data-testid="cost-profit-popup-close"
                    className="p-1 hover:bg-[#1C253C] rounded-sm"
                >
                    <X className="w-3.5 h-3.5 text-slate-400" />
                </button>
            </div>

            <div className="p-3 space-y-3">
                <Row label="Raw subtotal" value={rawSubtotal} />
                {rawMarkup > 0 && <Row label="Add profit" value={rawMarkup} />}
                <Row label="Raw cost" value={rawGrand} dim />

                <div className="rounded-sm border border-amber-500/30 bg-amber-500/5 p-2.5 space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-amber-300 inline-flex items-center gap-1">
                            <TrendingUp className="w-3.5 h-3.5" />
                            Add profit
                        </span>
                        <span
                            className="text-sm font-bold text-amber-300 tabular-nums"
                            data-testid="cost-profit-pct"
                        >
                            {pct.toFixed(1)}%
                        </span>
                    </div>
                    <input
                        type="range"
                        min="0"
                        max="60"
                        step="0.5"
                        value={pct}
                        onChange={(e) => setPct(Number(e.target.value))}
                        onMouseUp={commit}
                        onTouchEnd={commit}
                        data-testid="cost-profit-slider"
                        className="w-full accent-amber-400"
                    />
                    <Row
                        label="Profit on this job"
                        value={profitAmount}
                        strong
                        tone="amber"
                    />
                </div>

                <Row
                    label="Final client total"
                    value={finalTotal}
                    strong
                    emphasis
                    testId="cost-profit-final-total"
                />

                {report?.usage && (
                    <div className="pt-2 border-t border-[#1C253C]" />
                )}

                <div className="flex items-center gap-2 text-[11px] text-slate-400 pt-1 border-t border-[#1C253C]">
                    {report?.hide_profit_in_lines ? (
                        <>
                            <EyeOff className="w-3.5 h-3.5 text-amber-300 shrink-0" />
                            <span>
                                Margin is <b className="text-amber-200">blended</b> into each line — the
                                client's BOQ shows no separate profit row.
                            </span>
                        </>
                    ) : (
                        <>
                            <Eye className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                            <span>Margin shown as its own summary line on the client report.</span>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function Row({ label, value, dim = false, strong = false, emphasis = false, tone, testId }) {
    const toneCls =
        tone === "amber"
            ? "text-amber-300"
            : emphasis
              ? "text-white"
              : dim
                ? "text-slate-400"
                : "text-slate-200";
    return (
        <div className="flex items-center justify-between gap-3">
            <span className={`text-xs ${dim ? "text-slate-500" : "text-slate-300"}`}>{label}</span>
            <span
                data-testid={testId}
                className={`${strong ? "text-base font-bold" : "text-sm"} tabular-nums ${toneCls}`}
            >
                ${fmt(value)}
            </span>
        </div>
    );
}
